import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import axiosInstance from "../../helpers/axios";
import ApiDetailsDialog from "./ApiDetailsDialog";

const API_DETAILS_URL = "/api-analytics/api-details";
const CATEGORIES_URL = "/api-analytics/categories";
const SPEND_URL = "/api-analytics/api-details/spend";

// Chip colors go by position in the /categories list, so no category names are hardcoded here.
// Backend order today: ID Risk, Credit Risk, E-Sign, Collection, Disbursement, Notifications.
const CHIP_COLORS = [
  { backgroundColor: "#EFF6FF", color: "#1D4ED8" },
  { backgroundColor: "#FFFBEB", color: "#B45309" },
  { backgroundColor: "#F0FDFA", color: "#0F766E" },
  { backgroundColor: "#ECFDF3", color: "#027A48" },
  { backgroundColor: "#F5F3FF", color: "#6D28D9" },
  { backgroundColor: "#FDF2F8", color: "#BE185D" },
];

const HEAD_CELLS = [
  { label: "ID", width: "6%", sortKey: "id" },
  { label: "Name", width: "21%", sortKey: "name" },
  { label: "Provider", width: "14%", sortKey: "provider" },
  { label: "Rate/call", width: "11%", align: "right", sortKey: "costPerCall" },
  { label: "Category", width: "15%", sortKey: "category" },
  { label: "Status", width: "11%" },
  { label: "30-day calls", width: "11%", align: "right" },
  { label: "Actions", width: "11%", align: "right" },
];

const missingStyle = { color: "#D32028", fontStyle: "italic" };

// Same look as the stat cards in Admin Settings > Zone Management
function SummaryCard({ icon, color, tint, label, value, sub, valueColor }) {
  return (
    <Box
      sx={{
        flex: "1 1 220px",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "14px 20px",
        borderRadius: "14px",
        border: `1.5px solid ${color}33`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          backgroundColor: tint,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: valueColor }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 12, lineHeight: "16px" }}>
          {sub}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ApiRateCard() {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [callsByApiId, setCallsByApiId] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [provider, setProvider] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [sort, setSort] = useState({ key: "costPerCall", direction: "desc" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [conflictMessage, setConflictMessage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const latestRequest = useRef(0);

  const fetchApiDetails = () => {
    // 100 is the backend max, so the whole rate card loads on one page
    const params = { limit: 100 };
    if (search.trim()) params.search = search.trim();
    if (category) params.category = category;
    if (provider) params.provider = provider;
    if (activeOnly) params.activeOnly = true;

    // Only the newest request may update the table, so a slower older response can't show the wrong filter's rows
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError(null);
    axiosInstance
      .get(API_DETAILS_URL, { params })
      .then((res) => {
        if (requestId !== latestRequest.current) return;
        setRows(res?.data?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        if (requestId !== latestRequest.current) return;
        console.error("api-details fetch failed:", err);
        setError("Failed to load API rate card.");
        setLoading(false);
      });
  };

  // Unfiltered list for the summary cards and the Provider dropdown
  const fetchAllApis = () => {
    axiosInstance
      .get(API_DETAILS_URL, { params: { limit: 100 } })
      .then((res) => setAllRows(res?.data?.data || []))
      .catch((err) => console.error("api-details summary fetch failed:", err));
  };

  useEffect(() => {
    axiosInstance
      .get(CATEGORIES_URL)
      .then((res) => setCategories(res?.data?.data || []))
      .catch((err) => {
        console.error("categories fetch failed:", err);
        setSnackbar({ open: true, message: "Could not load categories. Please reload the page.", severity: "error" });
      });
    fetchAllApis();
    // Calls per API in the last 30 days, today included
    axiosInstance
      .get(SPEND_URL, {
        params: {
          from: moment().subtract(29, "days").format("YYYY-MM-DD"),
          to: moment().format("YYYY-MM-DD"),
          limit: 100,
        },
      })
      .then((res) =>
        setCallsByApiId(Object.fromEntries((res?.data?.data || []).map((item) => [item.apiId, item.callCount])))
      )
      .catch((err) => console.error("spend fetch failed:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wait until typing stops before calling the backend
  useEffect(() => {
    const timer = setTimeout(fetchApiDetails, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, provider, activeOnly]);

  const openDialog = (row) => {
    setEditingRow(row || null);
    setDialogOpen(true);
  };

  const handleSaved = (message) => {
    setSnackbar({ open: true, message, severity: "success" });
    fetchApiDetails();
    fetchAllApis();
  };

  const openDelete = (row) => {
    setDeleteTarget(row);
    setDeleteError(null);
    setConflictMessage(null);
  };

  const handleDelete = () => {
    setIsDeleting(true);
    setDeleteError(null);
    axiosInstance
      .delete(`${API_DETAILS_URL}/${deleteTarget.id}`)
      .then(() => {
        setDeleteTarget(null);
        setSnackbar({ open: true, message: "API deleted", severity: "success" });
        fetchApiDetails();
        fetchAllApis();
      })
      .catch((err) => {
        const message = err?.response?.data?.message;
        // 409 = calls older than the 30-day window: switch to the blocked state with the backend's message
        if (err?.response?.status === 409) setConflictMessage(message);
        else setDeleteError(message || "Could not delete API.");
      })
      .finally(() => setIsDeleting(false));
  };

  const handleSetInactive = () => {
    setIsDeleting(true);
    setDeleteError(null);
    axiosInstance
      .patch(`${API_DETAILS_URL}/${deleteTarget.id}`, { isActive: false })
      .then(() => {
        setDeleteTarget(null);
        setSnackbar({ open: true, message: "API set to inactive", severity: "success" });
        fetchApiDetails();
        fetchAllApis();
      })
      .catch((err) => setDeleteError(err?.response?.data?.message || "Could not update API."))
      .finally(() => setIsDeleting(false));
  };

  const deleteCalls = deleteTarget ? callsByApiId?.[deleteTarget.id] || 0 : 0;
  const deleteBlocked = deleteCalls > 0 || Boolean(conflictMessage);

  const handleSort = (key) =>
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));

  const sortValue = (row) => {
    const value = row[sort.key];
    return typeof value === "string" ? value.toLowerCase() : value ?? "";
  };

  const sortedRows = [...rows].sort((a, b) => {
    const x = sortValue(a);
    const y = sortValue(b);
    return (x > y ? 1 : x < y ? -1 : 0) * (sort.direction === "asc" ? 1 : -1);
  });

  const providers = [...new Set(allRows.map((row) => row.provider).filter(Boolean))].sort();
  const activeRows = allRows.filter((row) => row.isActive);
  const activeRates = activeRows.filter((row) => row.costPerCall != null);
  const averageRate = activeRates.length
    ? activeRates.reduce((sum, row) => sum + row.costPerCall, 0) / activeRates.length
    : null;
  const highest = activeRates.reduce((max, row) => (!max || row.costPerCall > max.costPerCall ? row : max), null);
  const incompleteCount = allRows.filter((row) => !row.provider || !row.category).length;

  const chipColor = (value) => CHIP_COLORS[categories.findIndex((item) => item.value === value)] || {};

  const formatRate = (value) =>
    value == null
      ? "—"
      : `₹${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div style={{ width: "100%", backgroundColor: "white", minHeight: "100vh" }}>
      <Box sx={{ padding: "24px" }}>
        <h2>API Rate Card</h2>

        <Box sx={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
          <SummaryCard
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 22 }} />}
            color="#027A48"
            tint="#ECFDF3"
            label="Active APIs"
            value={activeRows.length}
            sub={`${allRows.length - activeRows.length} inactive`}
          />
          <SummaryCard
            icon={<CurrencyRupeeIcon sx={{ fontSize: 22 }} />}
            color="#1D4ED8"
            tint="#EFF6FF"
            label="Avg rate / call"
            value={formatRate(averageRate)}
            sub="across active APIs"
          />
          <SummaryCard
            icon={<TrendingUpIcon sx={{ fontSize: 22 }} />}
            color="#d97706"
            tint="#fffbeb"
            label="Highest rate"
            value={formatRate(highest?.costPerCall)}
            sub={highest?.name || "—"}
          />
          <SummaryCard
            icon={<ErrorOutlineIcon sx={{ fontSize: 22 }} />}
            color="#D32028"
            tint="#fff0f0"
            label="Incomplete rows"
            value={incompleteCount}
            valueColor="#D32028"
            sub="missing provider/category"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search by name or provider..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Select
              size="small"
              displayEmpty
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All categories</MenuItem>
              {categories.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All providers</MenuItem>
              {providers.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              control={
                <Checkbox size="small" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
              }
              label="Active only"
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button variant="contained" onClick={() => openDialog()} sx={{ color: "#fff" }}>
              Add API
            </Button>
            {/* Same clear-filters button as the In Submission page (ProjectTables) */}
            <button
              onClick={() => {
                setSearch("");
                setCategory("");
                setProvider("");
                setActiveOnly(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "36px",
                width: "42px",
                backgroundColor: "#D22129",
                border: "none",
                borderRadius: "5px",
                color: "white",
                cursor: "pointer",
              }}
            >
              <RefreshIcon />
            </button>
          </Box>
        </Box>

        {loading && (
          <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            Loading API rate card…
          </div>
        )}
        {!loading && error && (
          <div style={{ textAlign: "center", padding: "40px", color: "#d32f2f" }}>
            {error}
          </div>
        )}
        {!loading && !error && (
          <Paper variant="outlined" sx={{ borderRadius: "8px" }}>
            <TableContainer>
              <Table sx={{ tableLayout: "fixed" }}>
                <TableHead sx={{ backgroundColor: "#F8F8F8" }}>
                  <TableRow>
                    {HEAD_CELLS.map((head) => (
                      <TableCell key={head.label} align={head.align || "left"} sx={{ width: head.width }}>
                        {head.sortKey ? (
                          <TableSortLabel
                            active={sort.key === head.sortKey}
                            direction={sort.key === head.sortKey ? sort.direction : "asc"}
                            onClick={() => handleSort(head.sortKey)}
                          >
                            {head.label}
                          </TableSortLabel>
                        ) : (
                          head.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((row) => (
                    <TableRow key={row.id} hover sx={row.isActive ? undefined : { "& td": { opacity: 0.5 } }}>
                      <TableCell>{row.id}</TableCell>
                      <TableCell sx={{ fontWeight: 500, wordBreak: "break-word" }}>{row.name}</TableCell>
                      <TableCell sx={{ wordBreak: "break-word" }}>
                        {row.provider || <span style={missingStyle}>⚠ no provider</span>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatRate(row.costPerCall)}
                      </TableCell>
                      <TableCell>
                        {row.category ? (
                          <Chip
                            size="small"
                            label={row.category}
                            sx={{ ...chipColor(row.category), fontWeight: 600 }}
                          />
                        ) : (
                          <span style={missingStyle}>⚠ no category</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: row.isActive ? "#027A48" : "#CBD5E1",
                            }}
                          />
                          {row.isActive ? "Active" : "Inactive"}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {callsByApiId ? (callsByApiId[row.id] || 0).toLocaleString("en-IN") : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" aria-label="Edit" onClick={() => openDialog(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" aria-label="Delete" onClick={() => openDelete(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={HEAD_CELLS.length}
                        align="center"
                        sx={{ color: "text.secondary", padding: "32px" }}
                      >
                        No APIs found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      <ApiDetailsDialog
        open={dialogOpen}
        row={editingRow}
        categories={categories}
        hasCalls={Boolean(editingRow) && (callsByApiId?.[editingRow.id] || 0) > 0}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={isDeleting ? undefined : () => setDeleteTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete API</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ marginBottom: "16px" }}>{deleteError}</Alert>}
          {deleteBlocked ? (
            <Alert severity="error" sx={{ marginBottom: "16px" }}>
              <strong>Cannot delete — this API has historical usage.</strong>{" "}
              {conflictMessage ||
                `It was used by ${deleteCalls.toLocaleString("en-IN")} calls in the last 30 days.`}{" "}
              Deleting it would orphan past cost records. Set it to Inactive instead to retire it while keeping
              its history.
            </Alert>
          ) : (
            <Box sx={{ marginBottom: "16px" }}>You are about to delete this API. This cannot be undone.</Box>
          )}
          <Box
            sx={{
              backgroundColor: "#F8F8F8",
              border: "1px solid #EAECF0",
              borderRadius: "8px",
              padding: "12px 14px",
              fontSize: 14,
            }}
          >
            {[
              ["Name", deleteTarget?.name],
              ["Provider", deleteTarget?.provider || "—"],
              ["Rate / call", formatRate(deleteTarget?.costPerCall)],
              ["30-day calls", callsByApiId ? deleteCalls.toLocaleString("en-IN") : "—"],
            ].map(([label, value]) => (
              <Box key={label} sx={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                <Box component="span" sx={{ color: "text.secondary" }}>{label}</Box>
                <span>{value}</span>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "8px 24px 20px",
            gap: "8px",
            "& .MuiButton-root": {
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              borderRadius: "8px",
              padding: "8px 16px",
              boxShadow: "none",
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
            sx={{ color: "#344054", borderColor: "#D0D5DD", "&:hover": { borderColor: "#98A2B3", backgroundColor: "#F9FAFB" } }}
          >
            Cancel
          </Button>
          {deleteBlocked && (
            <Button
              variant="contained"
              onClick={handleSetInactive}
              disabled={isDeleting || !deleteTarget?.isActive}
              sx={{ color: "#fff" }}
            >
              Set Inactive instead
            </Button>
          )}
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting || deleteBlocked}
            sx={{ "&.Mui-disabled": { backgroundColor: "#FEE4E2", color: "#FDA29B" } }}
          >
            {isDeleting && !deleteBlocked ? "Deleting..." : "Delete permanently"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
